/**
 * PublicPageSection — embeds a full Public Page layout (card/list/table/timeline)
 * inside the custom homepage. Uses the table's own view_config from the API,
 * optionally overridden by the section config. Supports section heading and
 * a "Show More" button that navigates to /p/:tableSlug.
 */

import React from 'react';
import { usePublicTableList } from '../../../hooks/usePublicTableView';
import DataSectionWrapper from '../DataSectionWrapper';
import PublicCardGrid    from '../../public/PublicCardGrid';
import PublicListFeed    from '../../public/PublicListFeed';
import PublicDataTable   from '../../public/PublicDataTable';
import PublicTimeline    from '../../public/PublicTimeline';
import PublicPageHeader  from '../../public/PublicPageHeader';

const PADDING_MAP = {
  none: 0,
  sm:   '0.75rem 1rem',
  md:   '1.5rem 2rem',
  lg:   '2.5rem 3rem',
};

function LoadingSkeleton({ viewType }) {
  if (viewType === 'list' || viewType === 'timeline') {
    return (
      <div>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ height: 56, background: '#f1f5f9', borderRadius: 8, marginBottom: 8, opacity: 1 - i * 0.15 }} />
        ))}
      </div>
    );
  }
  if (viewType === 'table') {
    return (
      <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ height: 40, background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }} />
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{ height: 44, background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #f1f5f9' }} />
        ))}
      </div>
    );
  }
  // card (default)
  return (
    <div className="row g-3">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="col-md-4">
          <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            <div style={{ height: 160, background: '#e2e8f0' }} />
            <div className="p-3">
              <div style={{ height: 14, background: '#e2e8f0', borderRadius: 4, marginBottom: 8, width: '65%' }} />
              <div style={{ height: 12, background: '#f1f5f9', borderRadius: 4 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function PublicPageSection({ config }) {
  const cfg = config || {};
  const tableSlug   = cfg.table_slug;
  const limit       = cfg.initial_count || 6;
  const detailEnabled = cfg.detail_enabled !== false;

  const bgPadding = PADDING_MAP[cfg.padding] || 0;

  // Synthesise a dataBinding shape so DataSectionWrapper can build the show-more link
  const dataBinding = { table_slug: tableSlug };

  const { data, isLoading, isError } = usePublicTableList(
    tableSlug,
    { page_size: limit },
  );

  if (!tableSlug) {
    return (
      <div className="text-center text-muted py-5">
        <i className="bi bi-window-stack" style={{ fontSize: 36, opacity: 0.25, display: 'block', marginBottom: 12 }} />
        Select a public table in the section settings.
      </div>
    );
  }

  const viewConfig   = data?.view_config   || {};
  const records      = data?.records       || [];
  const fields       = data?.fields        || [];
  const pagination   = data?.pagination    || {};
  const fieldMapping = viewConfig.field_mapping  || {};
  const layoutConfig = viewConfig.layout_config  || {};
  const branding     = viewConfig.branding       || {};

  // Honour view type override from the section config; fall back to table's default
  const viewType = cfg.view_type_override || viewConfig.view_type || 'card';

  const containerStyle = {
    background: cfg.bg_color || 'transparent',
    padding: bgPadding,
    borderRadius: cfg.bg_color ? 12 : 0,
  };

  const renderContent = () => {
    if (isLoading) return <LoadingSkeleton viewType={viewType} />;
    if (isError)   return <div className="text-muted text-center py-4">Failed to load data.</div>;
    if (records.length === 0) return <div className="text-muted text-center py-4">No records found.</div>;

    const sharedProps = {
      records,
      fields,
      mapping: fieldMapping,
      layoutConfig,
      detailEnabled,
      detailButtonLabel: viewConfig.interaction_config?.detail_button_label || '',
      tableSlug,
    };

    switch (viewType) {
      case 'list':     return <PublicListFeed  {...sharedProps} />;
      case 'table':    return <PublicDataTable {...sharedProps} />;
      case 'timeline': return <PublicTimeline  records={records} mapping={fieldMapping} layoutConfig={layoutConfig} detailEnabled={detailEnabled} tableSlug={tableSlug} />;
      default:         return <PublicCardGrid  {...sharedProps} />;
    }
  };

  return (
    <div style={containerStyle}>
      {/* Optional public page header (title + record count from branding config) */}
      {cfg.show_page_header && Object.keys(branding).length > 0 && (
        <PublicPageHeader
          branding={branding}
          recordCount={branding.show_record_count !== false ? pagination.total || records.length : null}
        />
      )}

      <DataSectionWrapper cfg={cfg} dataBinding={dataBinding}>
        {renderContent()}
      </DataSectionWrapper>
    </div>
  );
}
